import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Eye, EyeOff } from "lucide-react";
import { Logo } from "../components/Brand";
import { API, adminClient, errorText, money } from "../lib/site";

const Login = ({ onAuth }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem("ek_admin_token", data.token);
      onAuth();
    } catch (e2) {
      setErr(errorText(e2));
    }
  };
  return (
    <main className="min-h-screen grid place-items-center px-5">
      <form onSubmit={submit} data-testid="admin-login-modal" className="w-full max-w-sm rounded-3xl border border-[#e3ba73]/20 bg-[#1c1824] p-8">
        <Logo size={40} animate />
        <h1 className="mt-6 font-serif-d text-4xl">Author login</h1>
        <p className="mt-2 text-sm text-[#c4bbb0]">Private tips dashboard.</p>
        <input data-testid="admin-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password"
          className="mt-8 w-full bg-[#121016] border border-[#f4ede2]/15 rounded-xl px-4 py-3 focus:outline-none focus:border-[#e3ba73]" />
        {err && <p data-testid="admin-login-error" className="mt-3 text-sm text-[#ff9b8a]">{err}</p>}
        <button data-testid="admin-submit-button" className="mt-6 w-full rounded-full bg-[#e3ba73] text-[#121016] py-3 font-semibold hover:bg-[#f4d79e] transition-colors duration-300">Sign in</button>
        <Link to="/" className="block mt-6 text-center text-sm text-[#c4bbb0] hover:text-[#e3ba73]">← Back to tipping page</Link>
      </form>
    </main>
  );
};

const Kpi = ({ id, label, value }) => (
  <div data-testid={id} className="rounded-3xl border border-[#f4ede2]/10 bg-[#1c1824] p-6">
    <p className="font-mono-d text-[11px] tracking-[0.25em] uppercase text-[#c4bbb0]">{label}</p>
    <p className="mt-3 font-serif-d text-5xl text-[#e3ba73]">{value}</p>
  </div>
);

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["date", "name", "amount", "status", "public", "message", "payment_id"];
  return [head.join(","), ...rows.map((r) => [r.created_at, r.name, r.amount, r.status, r.public, r.message, r.payment_id].map(esc).join(","))].join("\n");
};

const Subscriptions = () => {
  const [subs, setSubs] = useState({ subscriptions: [], monthly_total: 0, active: 0 });
  const load = useCallback(async () => {
    try {
      const { data } = await adminClient().get("/admin/subscriptions");
      setSubs({ subscriptions: Array.isArray(data?.subscriptions) ? data.subscriptions : [], monthly_total: data?.monthly_total ?? 0, active: data?.active ?? 0 });
    } catch (e) { toast.error(errorText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const cancel = async (s) => {
    if (!window.confirm(`Cancel this ${money(s.amount)}/month subscription?`)) return;
    try { await adminClient().post(`/admin/subscriptions/${s.id}/cancel`); toast.success("Subscription will end after the current period"); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  return (
    <section data-testid="admin-subscriptions" className="mt-10 rounded-3xl border border-[#f4ede2]/10 bg-[#1c1824] p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-serif-d text-3xl">Monthly supporters</h2>
        <p data-testid="admin-monthly-total" className="font-serif-d text-3xl text-[#e3ba73]">{money(subs.monthly_total)}<span className="text-base text-[#c4bbb0]"> /month · {subs.active} active</span></p>
      </div>
      <div className="mt-5 space-y-2">
        {subs.subscriptions.length === 0 && <p className="text-sm text-[#c4bbb0]">No monthly supporters yet.</p>}
        {subs.subscriptions.map((s) => (
          <div key={s.id} data-testid="admin-subscription-row" className="flex flex-wrap items-center justify-between gap-3 border-t border-[#f4ede2]/10 pt-3 text-sm">
            <span className="text-[#c4bbb0]">Since {s.start_date}</span>
            <span className="font-serif-d text-xl text-[#e3ba73]">{money(s.amount)}/mo</span>
            <span>{s.canceled_date ? `Ends ${s.canceled_date}` : s.status}</span>
            {s.status === "ACTIVE" && !s.canceled_date && (
              <button data-testid={`admin-cancel-sub-${s.id}`} onClick={() => cancel(s)} className="rounded-full border border-[#ff9b8a]/40 text-[#ff9b8a] px-3 py-1 text-xs hover:bg-[#ff9b8a]/10">Cancel</button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const Dashboard = ({ onLogout }) => {
  const [data, setData] = useState({ tips: [], totals: {} });
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminClient().get("/admin/tips");
      setData({ tips: Array.isArray(d?.tips) ? d.tips : [], totals: d?.totals ?? {} });
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error(errorText(e));
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: d } = await adminClient().post("/admin/sync");
      toast.success(`Checked ${d.checked} pending · ${d.completed} confirmed`);
      await load();
    } catch (e) { toast.error(errorText(e)); }
    setSyncing(false);
  };

  const toggle = async (t) => {
    await adminClient().patch(`/admin/tips/${t.id}`, { public: !t.public });
    load();
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(data.tips)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tips.csv";
    a.click();
  };

  const t = data.totals;
  return (
    <main data-testid="admin-dashboard-container" className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3"><Logo size={30} /><span className="font-serif-d text-2xl">Even Keel · Tips</span></Link>
        <div className="flex flex-wrap gap-2">
          <button data-testid="admin-sync-button" onClick={sync} disabled={syncing} className="flex items-center gap-2 rounded-full border border-[#f4ede2]/20 px-4 py-2 text-sm hover:border-[#e3ba73]">
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> Check pending
          </button>
          <button data-testid="admin-export-csv-button" onClick={exportCsv} className="flex items-center gap-2 rounded-full border border-[#f4ede2]/20 px-4 py-2 text-sm hover:border-[#e3ba73]"><Download size={14} /> CSV</button>
          <button data-testid="admin-logout-button" onClick={onLogout} className="flex items-center gap-2 rounded-full bg-[#e3ba73] text-[#121016] px-4 py-2 text-sm font-semibold"><LogOut size={14} /> Log out</button>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi id="admin-kpi-total-tips" label="Total received" value={money(t.total)} />
        <Kpi id="admin-kpi-count" label="Tips" value={t.count ?? 0} />
        <Kpi id="admin-kpi-average" label="Average tip" value={money(t.average)} />
        <Kpi id="admin-kpi-pending" label="Unfinished checkouts" value={t.pending ?? 0} />
      </div>
      <Subscriptions />
      <div className="mt-10 rounded-3xl border border-[#f4ede2]/10 bg-[#1c1824] overflow-x-auto">
        <table data-testid="admin-tips-table" className="w-full text-sm min-w-[720px]">
          <thead className="text-left font-mono-d text-[11px] tracking-[0.2em] uppercase text-[#c4bbb0]">
            <tr>{["Date", "Reader", "Note", "Amount", "Status", "On wall"].map((h) => <th key={h} className="px-5 py-4 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.tips.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-[#c4bbb0]">No tips yet.</td></tr>}
            {data.tips.map((r) => (
              <tr key={r.id} data-testid="admin-tip-row" className="border-t border-[#f4ede2]/10 align-top">
                <td className="px-5 py-4 whitespace-nowrap text-[#c4bbb0]">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-5 py-4">{r.name || "Anonymous"}</td>
                <td className="px-5 py-4 max-w-xs text-[#d9d0c4]">{r.message || "—"}</td>
                <td className="px-5 py-4 font-serif-d text-xl text-[#e3ba73]">{money(r.amount)}{r.frequency === "monthly" && <span className="text-xs text-[#c4bbb0]"> /mo</span>}</td>
                <td className="px-5 py-4"><span className={`rounded-full px-3 py-1 text-xs ${r.status === "COMPLETED" ? "bg-[#2e5043] text-[#cdeedd]" : "bg-[#262130] text-[#c4bbb0]"}`}>{r.status === "COMPLETED" ? "Paid" : "Not completed"}</span></td>
                <td className="px-5 py-4">
                  <button data-testid={`admin-toggle-public-${r.id}`} onClick={() => toggle(r)} className="text-[#c4bbb0] hover:text-[#e3ba73]" aria-label="Toggle wall visibility">
                    {r.public ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(() => Boolean(localStorage.getItem("ek_admin_token")));
  const logout = useCallback(() => { localStorage.removeItem("ek_admin_token"); setAuthed(false); }, []);
  return authed ? <Dashboard onLogout={logout} /> : <Login onAuth={() => setAuthed(true)} />;
}
